import { useEffect, useState } from 'react'
import { Zap } from 'lucide-react'
import { billingApi, type GuildLimits, type GuildPremium } from '@/lib/billingApi'
import { ANIMATED_BANNER_TIER, BANNER_TIER, plural } from '@/lib/guildUi'
import { heightLabel } from '@/lib/screenShare'
import { cx } from '@/lib/cx'
import { describeError } from '@/lib/auth'
import { useAppStore } from '@/store/app'
import { avatarUrl } from '@/lib/avatar'
import { displayName } from '@/store/social'
import { Avatar } from '../Avatar'
import { StyledName } from '../StyledName'
import layer from '../settings/SettingsLayer.module.css'
import styles from './BoostSection.module.css'

const colorOf = (id: string) => `hsl(${Number(id.slice(-4)) % 360} 55% 50%)`
const longDate = (iso: string) => new Date(iso).toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' })

const ROWS: { name: string; value: (t: GuildLimits) => string }[] = [
  { name: 'Слоты эмодзи', value: (t) => String(t.emojis) },
  { name: 'Слоты стикеров', value: (t) => String(t.stickers) },
  { name: 'Звуки звуковой панели', value: (t) => String(t.sounds ?? 8) },
  { name: 'Баннер над каналами', value: (t) => (t.tier >= ANIMATED_BANNER_TIER ? 'Картинка или GIF' : t.tier >= BANNER_TIER ? 'Картинка' : '—') },
  { name: 'Качество звука', value: (t) => `${t.bitrate_kbps} кбит/с` },
  { name: 'Качество трансляции', value: (t) => `${heightLabel(t.stream_height)}, ${t.stream_fps} к/с` },
  { name: 'Файлы для всех участников', value: (t) => `${t.upload_mb} МБ` },
]

/** Вкладка «Буст»: уровень сервера, прогресс до следующего и что даёт каждый уровень. */
export function BoostSection({ guildId }: { guildId: string }) {
  const [state, setState] = useState<GuildPremium | null>(null)
  const [free, setFree] = useState(0)
  const [tiers, setTiers] = useState<GuildLimits[]>([])
  const [needs, setNeeds] = useState<number[]>([])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const openSettings = useAppStore((s) => s.openSettings)
  const closeGuildSettings = useAppStore((s) => s.closeGuildSettings)

  const load = () => {
    void billingApi.guild(guildId).then(setState).catch((err: unknown) => setError(describeError(err).message))
    void billingApi.status().then((s) => setFree(s.boosts_free)).catch(() => undefined)
    // пороги и лимиты всех уровней берём из витрины: они одни на всё приложение
    void billingApi.showcase().then((s) => { setTiers(s.tiers); setNeeds(s.boosts) }).catch(() => undefined)
  }
  useEffect(load, [guildId])

  const boost = () => {
    setBusy(true)
    setError(null)
    void billingApi
      .boost(guildId)
      .then(load)
      .catch((err: unknown) => setError(describeError(err).message))
      .finally(() => setBusy(false))
  }

  const need = state?.next_need ?? 0
  const need4 = (tier: number) => needs[tier - 1] ?? 0
  const progress = state && need ? Math.min(100, Math.round((state.boosts / need) * 100)) : 100

  return (
    <div>
      <h1 className={layer.sectionTitle}>Буст сервера</h1>
      <p className={layer.text}>
        Бусты участников поднимают уровень сервера. Уровень достаётся всем сразу: и тем, кто бустил, и тем, кто нет.
      </p>
      {error ? <p className={styles.error} role="alert">{error}</p> : null}

      <div className={styles.level}>
        <span className={styles.tier}><Zap size={18} /> Уровень {state?.tier ?? 0}</span>
        <span className={styles.count}>{state?.boosts ?? 0} {plural(state?.boosts ?? 0, 'буст', 'буста', 'бустов')}</span>
      </div>
      <div className={styles.bar}><span style={{ width: `${progress}%` }} /></div>
      {state?.next_tier ? (
        <p className={styles.hint}>
          До уровня {state.next_tier} — ещё {Math.max(0, need - state.boosts)}{' '}
          {plural(Math.max(0, need - state.boosts), 'буст', 'буста', 'бустов')}
        </p>
      ) : (
        <p className={styles.hint}>Максимальный уровень</p>
      )}

      <div className={styles.actions}>
        <button type="button" className={layer.button} disabled={busy || free === 0} onClick={boost}>
          {free > 0 ? `Поставить буст (свободно: ${free})` : 'Нет свободных бустов'}
        </button>
        <button type="button" className={styles.link} onClick={() => { closeGuildSettings(); openSettings('premium') }}>Где взять буст</button>
      </div>

      {/*
        * Таблица уровней. Отдельные числа вроде «100 эмодзи» ничего не говорят — смысл
        * появляется, когда рядом стоит, сколько было и сколько станет дальше. Текущий
        * уровень подсвечен, следующий обведён: к нему и идём.
        */}
      {tiers.length === 4 ? (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th scope="col">Что даёт уровень</th>
                {tiers.map((t) => (
                  <th
                    key={t.tier}
                    scope="col"
                    className={cx(t.tier === (state?.tier ?? 0) && styles.colNow, t.tier === (state?.tier ?? 0) + 1 && styles.colNext)}
                  >
                    <span className={styles.colTier}>{t.tier === 0 ? 'Без буста' : `Уровень ${t.tier}`}</span>
                    <span className={styles.colNeed}>
                      {t.tier === 0 ? '—' : `${need4(t.tier)} ${plural(need4(t.tier), 'буст', 'буста', 'бустов')}`}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map((row) => (
                <tr key={row.name}>
                  <th scope="row">{row.name}</th>
                  {tiers.map((t) => (
                    <td
                      key={t.tier}
                      className={cx(t.tier === (state?.tier ?? 0) && styles.colNow, t.tier === (state?.tier ?? 0) + 1 && styles.colNext)}
                    >
                      {row.value(t)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {state && state.boosters.length > 0 ? (
        <>
          <h2 className={layer.subTitle}>Кто бустит — {state.boosters.length}</h2>
          {/* больше бустов — выше; при равных порядок сервера: кто раньше начал */}
          <ul className={styles.boosters}>
            {[...state.boosters].sort((a, b) => b.boosts - a.boosts).map((b) => (
              <li key={b.user_id} className={styles.booster}>
                <Avatar initial={displayName(b.user).slice(0, 1).toUpperCase()} color={colorOf(b.user_id)} size={40} src={avatarUrl(b.user.id, b.user.avatar)} decoration={b.user.avatar_decoration_data} />
                <span className={styles.boosterText}>
                  <span className={styles.boosterName}><StyledName name={displayName(b.user)} style={b.user.display_name_styles} /></span>
                  {b.since ? <span className={styles.boosterSince}>Бустит с {longDate(b.since)}</span> : null}
                </span>
                <span className={styles.boosterCount}>
                  <Zap size={14} aria-hidden="true" /> {b.boosts} {plural(b.boosts, 'буст', 'буста', 'бустов')}
                </span>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  )
}
